import { useRef } from 'react';
import type { KanbanRaia, KanbanCartao, KanbanQuadro, OcupacaoWip, NivelPrioridadeKanban } from '@pieltec/contracts';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { KanbanCartaoComponent } from './kanban-cartao';
import { AlertTriangle, CheckCircle2, ChevronDown, ChevronRight, Settings2, TrendingUp } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../../../components/ui/collapsible';
import { useVirtualizer } from '@tanstack/react-virtual';
import { deveVirtualizar, rotuloAguardandoAlocacao, ALTURA_CARTAO_ESTIMADA } from '../../../lib/kanban/volume';
import { idDropRaia, type DestinoMover } from '../../../lib/kanban/movimento';
import { rotuloUnidade } from '../../../lib/kanban/unidade';

function formatarQuantidade(valor: number): string {
  const arredondado = Math.round(valor * 100) / 100;
  return Number.isInteger(arredondado)
    ? String(arredondado)
    : arredondado.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
}

export function OcupacaoWipLista({ ocupacao }: { ocupacao: readonly OcupacaoWip[] }) {
  if (ocupacao.length === 0) return null;
  return (
    <div className="flex flex-col gap-1 px-1">
      {ocupacao.map((item) => {
        const texto = `${formatarQuantidade(item.ocupado)}/${formatarQuantidade(item.limite)} ${rotuloUnidade(item.unidade)}`;
        if (item.estado === 'sobrecarregada') {
          return (
            <span
              key={item.unidade}
              className="inline-flex items-center gap-1 rounded border border-rose-300 bg-rose-50 px-1.5 py-0.5 text-[11px] font-medium text-rose-800"
            >
              <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
              Limite de WIP ultrapassado nesta célula ({texto}).
              {item.semMedida > 0 && (
                <span className="rounded border border-slate-300 bg-slate-100 px-1.5 py-0.5 font-medium text-slate-600">
                  sem medida: {item.semMedida}
                </span>
              )}
            </span>
          );
        }
        if (item.estado === 'proxima-limite') {
          return (
            <span
              key={item.unidade}
              className="inline-flex items-center gap-1 rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[11px] font-medium text-amber-800"
            >
              <TrendingUp className="h-3 w-3 shrink-0" aria-hidden="true" />
              {texto}
              {item.semMedida > 0 && (
                <span className="rounded border border-slate-300 bg-slate-100 px-1.5 py-0.5 font-medium text-slate-600">
                  sem medida: {item.semMedida}
                </span>
              )}
            </span>
          );
        }
        return (
          <span key={item.unidade} className="inline-flex flex-wrap items-center gap-1 text-[11px] text-slate-600">
            {texto}
            {item.semMedida > 0 && (
              <span className="rounded border border-slate-300 bg-slate-100 px-1.5 py-0.5 font-medium text-slate-600">
                sem medida: {item.semMedida}
              </span>
            )}
          </span>
        );
      })}
    </div>
  );
}

interface KanbanRaiaProps {
  raia: KanbanRaia;
  recolhida?: boolean;
  onAlternarRecolher?: () => void;
  onSelecionarCartao?: (cartao: KanbanCartao) => void;
  podeArrastar?: boolean;
  aceitaSoltura?: boolean;
  compativel?: boolean;
  emFoco?: boolean;
  quadro?: KanbanQuadro;
  onMover?: (cartao: KanbanCartao, destino: DestinoMover) => void;
  onPrioridade?: (cartao: KanbanCartao, nivel: NivelPrioridadeKanban) => void;
  onResolverConflito?: (cartao: KanbanCartao) => void;
  onDesbloquear?: (cartao: KanbanCartao) => void;
}

export function KanbanRaiaComponent({ 
  raia, 
  recolhida = false, 
  onAlternarRecolher, 
  onSelecionarCartao,
  podeArrastar = false,
  aceitaSoltura = false,
  compativel = false,
  emFoco = false,
  quadro,
  onMover,
  onPrioridade,
  onResolverConflito,
  onDesbloquear,
}: KanbanRaiaProps) {
  let tituloDisplay = raia.titulo;
  if (raia.tipo === 'A_ALOCAR') {
    tituloDisplay = rotuloAguardandoAlocacao(raia.cartoes.length);
  } else if (raia.tipo === 'CELULA_INATIVA' && !tituloDisplay.includes('célula inativa') && !tituloDisplay.includes('(inativa)')) {
    tituloDisplay += ' célula inativa';
  }

  let emptyText = 'Nenhum cartão nesta raia.';
  if (raia.tipo === 'A_ALOCAR') {
    emptyText = 'Nenhum cartão aguardando alocação.';
  }

  const isUnica = raia.tipo === 'UNICA';
  const isOpen = isUnica || !recolhida;
  const isVirtual = deveVirtualizar(raia.cartoes.length);
  const scrollRef = useRef<HTMLDivElement>(null);

  const virtualizer = useVirtualizer({
    count: raia.cartoes.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ALTURA_CARTAO_ESTIMADA,
    overscan: 8,
  });

  const { setNodeRef, isOver } = useDroppable({
    id: idDropRaia(raia.id),
    disabled: !podeArrastar,
  });
  const destacada = aceitaSoltura && (isOver || emFoco);

  return (
    <Collapsible
      open={isOpen}
      onOpenChange={(open) => {
        if (!isUnica && open === recolhida && onAlternarRecolher) {
          onAlternarRecolher();
        }
      }}
      className={`flex flex-col gap-2 bg-slate-100 p-2 rounded-md ${destacada ? 'ring-2 ring-[var(--color-primary)]' : ''}`}
    >
      {!isUnica && (
        <>
          <CollapsibleTrigger asChild>
            <button className="flex items-center justify-between w-full text-left px-1 py-1 hover:bg-slate-200/50 rounded-sm">
              <h5 className="text-xs font-semibold text-slate-700 uppercase flex-1">
                {tituloDisplay}
              </h5>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-slate-500 font-medium bg-slate-200/70 px-1.5 py-0.5 rounded">
                  {raia.cartoes.length}
                </span>
                {recolhida ? <ChevronRight className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
              </div>
            </button>
          </CollapsibleTrigger>
          <OcupacaoWipLista ocupacao={raia.ocupacao} />
          {raia.estufa?.ocupacao.some((item) => item.estado === 'sobrecarregada') && (
            <span className="inline-flex items-center gap-1 rounded border border-rose-300 bg-rose-50 px-1.5 py-0.5 text-[11px] font-medium text-rose-800">
              <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden="true" />
              Limite de WIP ultrapassado nesta estufa ({raia.estufa.codigo}).
            </span>
          )}
          {raia.celulaCodigo && (
            <a
              href={`/cadastros?aba=celulas&celula=${encodeURIComponent(raia.celulaCodigo)}`}
              className="inline-flex h-7 w-7 items-center justify-center rounded text-slate-500 hover:bg-slate-200"
              aria-label={`Cadastro da célula ${raia.celulaCodigo}`}
              onClick={(event) => event.stopPropagation()}
            >
              <Settings2 className="h-3.5 w-3.5" />
            </a>
          )}
        </>
      )}
      <CollapsibleContent>
        <div ref={setNodeRef}>
        <SortableContext items={raia.cartoes.map((cartao) => cartao.chave)} strategy={verticalListSortingStrategy}>
        {raia.cartoes.length === 0 ? (
          <div className="text-xs text-slate-400 p-2 text-center border border-dashed border-slate-300 rounded min-h-[60px]">
            {emptyText}
          </div>
        ) : isVirtual ? (
          <div ref={scrollRef} className="max-h-[600px] overflow-y-auto">
            <div
              style={{
                height: `${virtualizer.getTotalSize()}px`,
                width: '100%',
                position: 'relative',
              }}
            >
              {virtualizer.getVirtualItems().map((virtualItem) => {
                const cartao = raia.cartoes[virtualItem.index];
                if (!cartao) return null;
                return (
                  <div
                    key={cartao.chave}
                    data-index={virtualItem.index}
                    ref={virtualizer.measureElement}
                    style={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      transform: `translateY(${virtualItem.start}px)`,
                      paddingBottom: '8px'
                    }}
                  >
                    <KanbanCartaoComponent
                      cartao={cartao}
                      onSelecionar={onSelecionarCartao}
                      podeArrastar={podeArrastar}
                      quadro={quadro}
                      onMover={onMover}
                      onPrioridade={onPrioridade}
                      onResolverConflito={onResolverConflito}
                      onDesbloquear={onDesbloquear}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        ) : (
          <div className={isUnica ? "flex flex-col gap-2 min-h-[60px]" : "flex flex-col gap-2"}>
            {raia.cartoes.map((cartao) => (
              <KanbanCartaoComponent
                key={cartao.chave}
                cartao={cartao}
                onSelecionar={onSelecionarCartao}
                podeArrastar={podeArrastar}
                quadro={quadro}
                onMover={onMover}
                onPrioridade={onPrioridade}
                onResolverConflito={onResolverConflito}
                onDesbloquear={onDesbloquear}
              />
            ))}
          </div>
        )}
        </SortableContext>
        {compativel && (
          <span className="mt-1 inline-flex items-center gap-1 text-[11px] font-medium text-slate-700">
            <CheckCircle2 className="h-3 w-3 shrink-0" aria-hidden="true" />
            compatível
          </span>
        )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
